import { useAuth } from '../contexts/auth-context';
import { useQuery } from '@tanstack/react-query';
import { getOrganizersByUserAccess, getMyOrganizations } from '../apiEndpoints';

export interface UserRole {
    role: 'admin' | 'eventmanager' | 'usher';
    organizerId: string;
    organizerName: string;
}

export interface UserRolesData {
    roles: UserRole[];
    ownedOrganizerIds: string[];
}

/**
 * Hook to get user's roles across all organizers and owned organizer IDs
 */
export function useUserRoles() {
    const { user } = useAuth();

    return useQuery<UserRolesData>({
        queryKey: ['userRoles', user?.email, user?.userId],
        queryFn: async () => {
            if (!user?.email || !user?.userId) {
                return { roles: [], ownedOrganizerIds: [] };
            }

            // Fetch organizers where user has authorization emails
            const response = await getOrganizersByUserAccess(user.email);
            const organizers = response.data || [];

            // Extract roles from each organizer's authorization emails
            const userRoles: UserRole[] = [];

            organizers.forEach((organizer: any) => {
                const userAuth = organizer.authorizationEmails?.find(
                    (auth: any) => auth.email === user.email
                );

                if (userAuth) {
                    userRoles.push({
                        role: userAuth.role,
                        organizerId: organizer._id,
                        organizerName: organizer.orgName
                    });
                }
            });

            // Fetch organizations where user is the owner
            const myOrgsResponse = await getMyOrganizations();
            const myOrganizations = myOrgsResponse?.data || [];

            // Find organizers owned by the user (where userId matches)
            const ownedOrganizerIds: string[] = [];
            myOrganizations.forEach((org: any) => {
                const orgUserId = org.userId?.toString() || org.userId;
                const currentUserId = user.userId?.toString() || user.userId;
                if (orgUserId === currentUserId) {
                    ownedOrganizerIds.push(org._id?.toString() || org._id);
                }
            });

            return { roles: userRoles, ownedOrganizerIds };
        },
        enabled: !!user?.email && !!user?.userId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: false,
    });
}

/**
 * Check if user has edit access to events
 * - Organizer Owner: Always has edit access to events for their organizer
 * - Admin/Event Manager: Has edit access to events they're authorized for
 * - Usher: No edit access (usher can only check-in)
 */
export function hasEditAccess(userRoles: UserRole[], eventOrganizerId?: string, ownedOrganizerIds: string[] = []): boolean {
    // Check if user is the owner of the organizer
    if (eventOrganizerId && ownedOrganizerIds.includes(eventOrganizerId)) {
        return true;
    }

    // Check if user has admin or eventmanager role for this organizer
    // Usher role is explicitly excluded from edit access
    if (eventOrganizerId) {
        const userRole = userRoles.find(role => role.organizerId === eventOrganizerId);
        return userRole ? ['admin', 'eventmanager'].includes(userRole.role) : false;
    }

    return false;
}

/**
 * Check if user has full management access (everything except check-in)
 * - Organizer Owner: Always has full access to events for their organizer
 * - Admin/Event Manager: Has full access to events they're authorized for
 * - Usher: No management access (usher can only check-in)
 */
export function hasFullAccess(userRoles: UserRole[], eventOrganizerId?: string, ownedOrganizerIds: string[] = []): boolean {
    // Check if user is the owner of the organizer
    if (eventOrganizerId && ownedOrganizerIds.includes(eventOrganizerId)) {
        return true;
    }

    // Check if user has admin or eventmanager role for this organizer
    // Usher role is explicitly excluded from full access
    if (eventOrganizerId) {
        const userRole = userRoles.find(role => role.organizerId === eventOrganizerId);
        return userRole ? ['admin', 'eventmanager'].includes(userRole.role) : false;
    }

    return false;
}

/**
 * Check if user has check-in access to events
 * - All roles (admin, eventmanager, usher) can check-in
 * - Organizer Owner can always check-in
 */
export function hasCheckinAccess(userRoles: UserRole[], eventOrganizerId?: string, ownedOrganizerIds: string[] = []): boolean {
    // Check if user is the owner of the organizer
    if (eventOrganizerId && ownedOrganizerIds.includes(eventOrganizerId)) {
        return true;
    }

    // Check if user has any role for this organizer
    if (eventOrganizerId) {
        const userRole = userRoles.find(role => role.organizerId === eventOrganizerId);
        return userRole ? ['admin', 'eventmanager', 'usher'].includes(userRole.role) : false;
    }

    return false;
}

/**
 * Get user's highest role for an organizer
 */
export function getUserRoleForOrganizer(userRoles: UserRole[], organizerId: string): 'admin' | 'eventmanager' | 'usher' | null {
    const userRole = userRoles.find(role => role.organizerId === organizerId);
    return userRole ? userRole.role : null;
}

/**
 * Check if user is usher only (no edit permissions)
 */
export function isUsherOnly(userRoles: UserRole[]): boolean {
    return userRoles.length > 0 && userRoles.every(role => role.role === 'usher');
}

/**
 * Check if user has any management access (admin or eventmanager)
 * Ushers are excluded from all management functions
 */
export function hasManagementAccess(userRoles: UserRole[], eventOrganizerId?: string, ownedOrganizerIds: string[] = []): boolean {
    // Check if user is the owner of the organizer
    if (eventOrganizerId && ownedOrganizerIds.includes(eventOrganizerId)) {
        return true;
    }

    // Check if user has admin or eventmanager role for this organizer
    if (eventOrganizerId) {
        const userRole = userRoles.find(role => role.organizerId === eventOrganizerId);
        return userRole ? ['admin', 'eventmanager'].includes(userRole.role) : false;
    }

    return false;
}

/**
 * Check if user can view event management interface
 * Ushers should only see check-in interface
 */
export function canViewEventManagement(userRoles: UserRole[], eventOrganizerId?: string, ownedOrganizerIds: string[] = []): boolean {
    return hasManagementAccess(userRoles, eventOrganizerId, ownedOrganizerIds);
}
